import React, { useState, useEffect } from 'react';
import { X, Truck, User, MapPin, Loader, ShoppingBag, TriangleAlert as AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { CART_CHECKOUT_URL, GET_SAVED_ADDRESSES_URL } from '../../config/api';
import './CartCheckout.css';

interface CartSummary {
  total_items: number;
  subtotal: number;
  delivery_fee: number;
  tax: number;
  grand_total: number;
}

interface CartCheckoutProps {
  summary: CartSummary | null;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

interface SavedAddress {
  address_id: string;
  label: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  is_default: boolean;
}

const CartCheckout: React.FC<CartCheckoutProps> = ({ summary, onClose, onSuccess }) => {
  const [deliveryType, setDeliveryType] = useState<'delivery_partner' | 'self_pickup'>('delivery_partner');
  const paymentMethod = 'cod' as const;
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customAddress, setCustomAddress] = useState('');

  useEffect(() => {
    const load = async () => {
      setAddressesLoading(true);
      try {
        const res = await authenticatedFetch(GET_SAVED_ADDRESSES_URL);
        if (res.ok) {
          const json = await res.json();
          const addrs: SavedAddress[] = json.saved_addresses ?? [];
          setSavedAddresses(addrs);
          const defaultAddr = addrs.find(a => a.is_default) ?? addrs[0];
          if (defaultAddr) setSelectedAddressId(defaultAddr.address_id);
        }
      } catch {
        // non-critical
      } finally {
        setAddressesLoading(false);
      }
    };
    load();
  }, []);

  const getDeliveryAddress = (): string => {
    if (showCustomInput || savedAddresses.length === 0) return customAddress.trim();
    const addr = savedAddresses.find(a => a.address_id === selectedAddressId);
    if (!addr) return customAddress.trim();
    return [addr.address_line, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ');
  };

  const validate = (): boolean => {
    if (deliveryType === 'delivery_partner') {
      const addr = getDeliveryAddress();
      if (!addr) {
        setError('Please select or enter your delivery address.');
        return false;
      }
      if (addr.length < 10) {
        setError('Please provide a complete delivery address (at least 10 characters).');
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const body: Record<string, string> = {
        payment_method: paymentMethod,
        delivery_type: deliveryType,
      };
      body.delivery_address = deliveryType === 'delivery_partner' ? getDeliveryAddress() : '';

      const res = await authenticatedFetch(CART_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const json = await res.json();

      if (res.ok && json.success) {
        onSuccess({
          ...json,
          delivery_type: deliveryType,
          payment_method: paymentMethod,
          delivery_address: deliveryType === 'delivery_partner' ? getDeliveryAddress() : undefined,
          pickup_address: deliveryType === 'self_pickup' ? json.pickup_details?.pickup_address : undefined,
          google_map_link: deliveryType === 'self_pickup' ? json.pickup_details?.google_map_link : undefined,
        });
      } else {
        if (res.status === 422) {
          setError(json.detail?.[0]?.msg || 'Validation error. Please check your details.');
        } else {
          setError(json.message || 'Checkout failed. Please try again.');
        }
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="checkout-overlay" onClick={handleOverlayClick}>
      <div className="checkout-modal">
        <div className="checkout-header">
          <button className="checkout-close-btn" onClick={onClose} disabled={isSubmitting}>
            <X size={22} />
          </button>
          <h2 className="checkout-title">Checkout</h2>
          <p className="checkout-subtitle">Complete your order details below</p>
        </div>

        <form className="checkout-form" onSubmit={handleSubmit}>
          {summary && (
            <div className="checkout-summary-strip">
              <span>{summary.total_items} item{summary.total_items !== 1 ? 's' : ''}</span>
              <span className="checkout-summary-total">₹{summary.grand_total.toFixed(2)}</span>
            </div>
          )}

          {/* Delivery Type */}
          <div className="checkout-section">
            <h3 className="checkout-section-title">
              <Truck size={18} />
              Delivery Type
            </h3>
            <div className="checkout-options">
              <label className={`checkout-option ${deliveryType === 'delivery_partner' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="delivery_partner"
                  checked={deliveryType === 'delivery_partner'}
                  onChange={() => setDeliveryType('delivery_partner')}
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <Truck size={18} />
                  <div>
                    <span className="option-title">Kitchen Delivery</span>
                    <span className="option-desc">Kitchen owner delivers to your address (subject to confirmation)</span>
                  </div>
                </div>
              </label>
              <label className={`checkout-option ${deliveryType === 'self_pickup' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="deliveryType"
                  value="self_pickup"
                  checked={deliveryType === 'self_pickup'}
                  onChange={() => setDeliveryType('self_pickup')}
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <User size={18} />
                  <div>
                    <span className="option-title">Self Pickup</span>
                    <span className="option-desc">Pick up from cook's location</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Self-Pickup notice */}
          {deliveryType === 'self_pickup' && (
            <div className="checkout-pickup-notice">
              <MapPin size={16} />
              <span>
                You will pick up this order directly from the cook's kitchen.
                The exact pickup address and a map link will be shown once your order is placed.
              </span>
            </div>
          )}

          {/* Delivery Address */}
          {deliveryType === 'delivery_partner' && (
            <div className="checkout-section">
              <h3 className="checkout-section-title">
                <MapPin size={18} />
                Delivery Address
              </h3>

              {addressesLoading ? (
                <div className="checkout-addr-loading">
                  <Loader size={16} className="checkout-addr-spinner" />
                  <span>Loading saved addresses...</span>
                </div>
              ) : savedAddresses.length > 0 && !showCustomInput ? (
                <>
                  <div className="checkout-addr-list">
                    {savedAddresses.map(addr => (
                      <label
                        key={addr.address_id}
                        className={`checkout-addr-card${selectedAddressId === addr.address_id ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="savedAddress"
                          checked={selectedAddressId === addr.address_id}
                          onChange={() => { setSelectedAddressId(addr.address_id); if (error) setError(''); }}
                          disabled={isSubmitting}
                        />
                        <div className="checkout-addr-card-body">
                          <div className="checkout-addr-card-top">
                            <span className="checkout-addr-label">{addr.label}</span>
                            {addr.is_default && <span className="checkout-addr-default">Default</span>}
                          </div>
                          <span className="checkout-addr-line">
                            {[addr.address_line, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="checkout-addr-toggle"
                    onClick={() => setShowCustomInput(true)}
                  >
                    <ChevronDown size={14} />
                    Enter a different address
                  </button>
                </>
              ) : (
                <>
                  {savedAddresses.length > 0 && (
                    <button
                      type="button"
                      className="checkout-addr-toggle"
                      onClick={() => { setShowCustomInput(false); if (error) setError(''); }}
                    >
                      <ChevronUp size={14} />
                      Use a saved address
                    </button>
                  )}
                  <textarea
                    className={`checkout-address-input ${error && !customAddress.trim() ? 'input-error' : ''}`}
                    value={customAddress}
                    onChange={e => { setCustomAddress(e.target.value); if (error) setError(''); }}
                    placeholder="Enter your complete delivery address including house number, street, area, city and postal code..."
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <p className="checkout-address-hint">
                    Example: "123 Main Street, Near Central Park, Mumbai, 400001"
                  </p>
                </>
              )}
            </div>
          )}

          {/* Payment Method */}
          <div className="checkout-section">
            <h3 className="checkout-section-title">
              <ShoppingBag size={18} />
              Payment Method
            </h3>
            <div className="checkout-options">
              <label className="checkout-option selected">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked
                  readOnly
                  disabled={isSubmitting}
                />
                <div className="option-content">
                  <span className="option-icon">💵</span>
                  <div>
                    <span className="option-title">Cash on Delivery</span>
                    <span className="option-desc">Pay when you receive your order</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {error && (
            <div className="checkout-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="checkout-actions">
            <button type="button" className="checkout-cancel-btn" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="checkout-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader size={16} className="checkout-spinner" />
                  Placing Order...
                </>
              ) : (
                <>
                  <ShoppingBag size={16} />
                  Place Order{summary ? ` — ₹${summary.grand_total.toFixed(2)}` : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CartCheckout;
